export const INVOICE_UPLOAD_ACCEPT =
    'image/jpeg,image/jpg,image/png,image/tiff,image/tif,.tif,.tiff,application/pdf';

export const INVOICE_IMAGE_ADD_ACCEPT =
    'image/jpeg,image/png,image/webp,image/tiff,.jpg,.jpeg,.png,.webp,.tif,.tiff';

export const TIFF_PREVIEW_PLACEHOLDER =
    'data:image/svg+xml,' +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="100%" height="100%" fill="#EDF2F7"/><text x="50%" y="54%" text-anchor="middle" font-size="16" fill="#4A5568" font-family="sans-serif">TIF</text></svg>',
    );

const PDF_MIMES = new Set(['application/pdf']);
const JPEG_MIMES = new Set(['image/jpeg', 'image/jpg']);
const PNG_MIMES = new Set(['image/png']);
const TIFF_MIMES = new Set(['image/tiff', 'image/tif']);

export type InvoiceSignedUrlKind = 'image' | 'tiff' | 'pdf';

function mimeOf(file: File): string {
    return (file.type || '').toLowerCase().trim();
}

function nameOf(file: File): string {
    return (file.name || '').toLowerCase();
}

export function isPdfFile(file: File): boolean {
    return PDF_MIMES.has(mimeOf(file)) || nameOf(file).endsWith('.pdf');
}

export function isTiffFile(file: File): boolean {
    const t = mimeOf(file);
    if (TIFF_MIMES.has(t)) return true;
    const name = nameOf(file);
    return name.endsWith('.tif') || name.endsWith('.tiff');
}

export function isJpegOrPngFile(file: File): boolean {
    const t = mimeOf(file);
    if (JPEG_MIMES.has(t) || PNG_MIMES.has(t)) return true;
    const name = nameOf(file);
    return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
}

/** Raster files the create/bulk upload UI accepts (JPEG, PNG, TIFF). */
export function isInvoiceImageFile(file: File): boolean {
    return isJpegOrPngFile(file) || isTiffFile(file);
}

export function isAcceptedInvoiceUploadFile(file: File): boolean {
    return isPdfFile(file) || isInvoiceImageFile(file);
}

export function signedUrlKindForFile(file: File): InvoiceSignedUrlKind | null {
    if (isPdfFile(file)) return 'pdf';
    if (isTiffFile(file)) return 'tiff';
    if (isJpegOrPngFile(file) || mimeOf(file) === 'image/webp' || nameOf(file).endsWith('.webp')) {
        return 'image';
    }
    return null;
}

export function contentTypeForInvoiceSignedPut(kind: InvoiceSignedUrlKind, file: File): string {
    if (kind === 'tiff') return 'image/tiff';
    if (kind === 'pdf') return 'application/pdf';
    return file.type || 'image/jpeg';
}

export async function putInvoiceFileToSignedUrl(
    signedUrl: string,
    file: File,
    kind: InvoiceSignedUrlKind,
): Promise<void> {
    const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': contentTypeForInvoiceSignedPut(kind, file),
        },
    });
    if (!response.ok) {
        throw new Error(`Failed to upload to S3: ${response.status}`);
    }
}
